import React, { useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import Section from "../../../layout/Section";
import Button from "../../../common/inputs/Button";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import GewichtBalken from "../components/GewichtBalken";
import { AdminReifegradSettings } from "../../../../services/api/admin";
import { useAdminReifegradSettings } from "../hooks/useAdminReifegradSettings";
import { useToast } from "../../../../contexts/ToastContext";
import { colors } from "../../../../styles/colors";

interface ReifegradScoreSectionProps {
  orgId: string;
}

type FormState = Record<keyof AdminReifegradSettings, string>;

const KATEGORIEN: { key: keyof AdminReifegradSettings; label: string }[] = [
  { key: "gewicht_flaeche", label: "Fläche" },
  { key: "gewicht_planungsrecht", label: "Planungsrecht" },
  { key: "gewicht_genehmigung", label: "Genehmigung" },
  { key: "gewicht_ausschreibung", label: "Ausschreibung" },
  { key: "gewicht_finanzierung", label: "Finanzierung" },
  { key: "gewicht_bau", label: "Bau" },
];

const numToStr = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return "";
  return String(value);
};

const toFormState = (settings: AdminReifegradSettings): FormState =>
  KATEGORIEN.reduce((acc, { key }) => {
    acc[key] = numToStr((settings[key] ?? 0) * 100);
    return acc;
  }, {} as FormState);

const GEWICHT_SUMME_TOLERANZ = 0.05;

const ReifegradScoreSection: React.FC<ReifegradScoreSectionProps> = ({
  orgId,
}) => {
  const { settings, loading, save } = useAdminReifegradSettings(orgId);
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm(toFormState(settings));
    }
  }, [settings]);

  if (loading || !form) {
    return null;
  }

  const setField = (field: keyof AdminReifegradSettings) => (value: string) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const gewichtSumme = KATEGORIEN.reduce(
    (sum, { key }) => sum + (parseFloat(form[key]) || 0),
    0,
  );
  const gewichtSummeValide =
    Math.abs(gewichtSumme - 100) <= GEWICHT_SUMME_TOLERANZ;

  const handleSave = async () => {
    if (!gewichtSummeValide) {
      showError("Die Gewichte müssen in Summe 100 % ergeben");
      return;
    }
    setSaving(true);
    try {
      await save(
        KATEGORIEN.reduce((acc, { key }) => {
          acc[key] = (parseFloat(form[key]) || 0) / 100;
          return acc;
        }, {} as AdminReifegradSettings),
      );
      showSuccess("Reifegrad-Score-Einstellungen gespeichert");
    } catch {
      showError(
        "Reifegrad-Score-Einstellungen konnten nicht gespeichert werden",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section
      title="Reifegrad-Score"
      actions={
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          Speichern
        </Button>
      }
    >
      <Box sx={{ mb: 5 }}>
        <GewichtBalken
          segments={KATEGORIEN.map(({ key, label }, index) => ({
            key,
            label,
            value: parseFloat(form[key]) || 0,
            color: colors.scoreWeighting[index],
          }))}
          onChange={(updates) => {
            setForm((prev) => {
              if (!prev) return prev;
              const next = { ...prev };
              Object.entries(updates).forEach(([key, value]) => {
                next[key as keyof AdminReifegradSettings] = String(value);
              });
              return next;
            });
          }}
        />
        <Grid container spacing={3} sx={{ alignItems: "center", mt: 2 }}>
          {KATEGORIEN.map(({ key, label }, index) => (
            <Grid key={key} size={{ xs: 12, md: 4 }} sx={{ minWidth: 260 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "3px",
                    backgroundColor: colors.scoreWeighting[index],
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <InputFieldNumber
                    label={label}
                    value={form[key]}
                    onChange={setField(key)}
                    suffix="%"
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      {!gewichtSummeValide && (
        <Typography
          variant="body2"
          sx={{ color: colors.status.error.main, mt: 1 }}
        >
          Die Gewichte ergeben aktuell {gewichtSumme.toFixed(1)} % – sie müssen
          in Summe 100 % ergeben.
        </Typography>
      )}
    </Section>
  );
};

export default ReifegradScoreSection;
